import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  cartService,
  type GoodsOption,
  type GetCartMetaResponse,
  type GoodsOptionsResponse,
} from 'shared/src/api';
import { useGoodsSno } from '../../../hooks/useGoodsSno';
import { queryKey } from 'shared/src/constant';
import { queryKey as goodsQueryKey } from '../../../constant/queryKey';
import { isNil } from 'shared/src/utils';

export const useAddToCart = (selected: GoodsOption[]) => {
  const goodsSno = useGoodsSno();
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ isForce = false }: { isForce?: boolean } = {}) => {
      return cartService.postAddToCart({
        goods_sno: goodsSno,
        goods_options: selected.map(option => ({
          is_force: isForce,
          delivery_type: option.delivery_type,
          count: option.purchase_count ?? 1,
          text_option: option.message ?? null,
          sno: option.sno,
        })),
      });
    },
    throwOnError: false,
    onSuccess: data => {
      client.setQueryData<{ data: GetCartMetaResponse }>(
        queryKey.cartMeta,
        oldData => {
          if (isNil(oldData)) return oldData;
          return {
            ...oldData,
            carts_count: data.carts_count,
          };
        },
      );

      const selectedOptionSno = selected[0]?.sno;

      client.setQueryData<GoodsOptionsResponse>(
        goodsQueryKey.goodsOptions(goodsSno, {
          depth: 2,
          selected_option_sno: selectedOptionSno,
        }),
        oldData => {
          if (isNil(oldData)) return oldData;

          return {
            ...oldData,
            option_components: oldData.option_components.map(option => {
              if (option.goods_option?.sno === selectedOptionSno) {
                return {
                  ...option,
                  goods_option: {
                    ...option.goods_option,
                    stock: Math.max(0, option.goods_option.stock - 1),
                    is_soldout:
                      Math.max(0, option.goods_option.stock - 1) === 0,
                  },
                };
              }
              return option;
            }),
          };
        },
      );
    },
  });
};
